"use client";
import { useEffect } from "react";

// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getMessaging, onMessage } from "firebase/messaging";
import toast from "react-hot-toast";
import { Bell } from "lucide-react";
import NotificationToast from "../Toasts/NotificationToast";
import {
  incrementNotificationCount,
  incrementChatCount,
  resetChatCount,
} from "@/lib/features/notificationSlice";
import { useDispatch } from "react-redux";
import { usePushNotificationsEmitter } from "@/contexts/PushNotificationContext";
import { useAuth } from "@/contexts/authContext";
import { usePathname } from "next/navigation";
// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

export default function FirebaseNotificationSubscriber() {
  const dispatch = useDispatch();
  const emitPushEvent = usePushNotificationsEmitter();
  const { user } = useAuth();
  const pathname = usePathname();
  const playSound = () => {
    const audio = new Audio("/public/foreground-notification-1.mp3");
    audio.play();
  };

  useEffect(() => {
    // Only initialize messaging if Firebase messaging is supported
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return;
    }

    try {
      const messaging = getMessaging(app);

      // Handle incoming messages
      onMessage(messaging, (payload) => {
        const payloadUserId = (payload.data as any)?.user_id as
          | string
          | undefined;
        if (payloadUserId && user?.id && payloadUserId != user.id) {
          return;
        }

        const chatId = (payload.data as any)?.chat_id as string | undefined;
        const isOnSameChat = Boolean(
          chatId && pathname && pathname === `/chats/${chatId}`
        );
        const isOnAnyChatPage = Boolean(
          pathname && pathname.startsWith("/chats/") && pathname !== "/chats"
        );

        // Don't increment count if user is on any chat page
        if (!isOnAnyChatPage) {
          if (chatId) {
            dispatch(incrementChatCount());
          } else {
            dispatch(incrementNotificationCount());
          }
        } else {
        }
        if (payload?.data) {
          try {
            emitPushEvent(payload.data as any);
          } catch (e) {
            console.error("Error emitting push event", e);
          }
        }

        if (isOnSameChat) {
          return; // Do not show toast if already viewing this chat
        }

        playSound();
        const notificationBody = payload.data?.msg || "New Updates!";
        const deepLink = chatId
          ? `/chats/${chatId}`
          : (() => {
              const raw = (payload.data as any)?.url as string | undefined;
              if (!raw) return "/notification";
              try {
                const u = new URL(raw);
                return u.pathname + (u.search || "");
              } catch {
                return "/notification";
              }
            })();
        toast.custom((t) => (
          <NotificationToast
            toastId={t.id}
            title={""}
            body={notificationBody}
            url={deepLink}
          />
        ));
      });
    } catch (error) {
      console.error("Error setting up Firebase messaging:", error);
    }
  }, [dispatch, emitPushEvent, pathname, user?.id]);

  // Listen for messages forwarded by the service worker (background FCM)
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator))
      return;
    const handler = (event: MessageEvent) => {
      const data: any = event?.data || {};
      if (data?.type !== "FCM_MESSAGE") return;

      const payload = data?.data || {};
      const payloadUserId = payload?.user_id as string | undefined;
      if (payloadUserId && user?.id && payloadUserId == user.id) {
        return;
      }

      const chatId = payload?.chat_id as string | undefined;
      const isOnSameChat = Boolean(
        chatId && pathname && pathname === `/chats/${chatId}`
      );
      const isOnAnyChatPage = Boolean(
        pathname && pathname.startsWith("/chats/") && pathname !== "/chats"
      );

      // Don't increment count if user is on any chat page
      if (!isOnAnyChatPage) {
        if (chatId) {
          dispatch(incrementChatCount());
        } else {
          dispatch(incrementNotificationCount());
        }
      } else {
      }

      try {
        emitPushEvent(payload);
      } catch (e) {
        console.error("Error emitting push event from service worker", e);
      }

      // Do not show toast here; when permission is denied we only update UI
      // If needed later, we can optionally show an in-app toast when not on same chat
      if (!isOnSameChat) {
      }
    };
    navigator.serviceWorker.addEventListener("message", handler as any);
    return () =>
      navigator.serviceWorker.removeEventListener("message", handler as any);
  }, [dispatch, emitPushEvent, pathname, user?.id]);

  // Reset chat count when navigating to any chat page
  useEffect(() => {
    if (pathname && pathname.startsWith("/chats/") && pathname !== "/chats") {
      dispatch(resetChatCount());
    }
  }, [pathname, dispatch]);

  return <></>;
}
